import React from 'react';
import Switch from "rc-switch";
import {useModelFormEngine} from "../common/hooks/useModelFormEngine";
import {EmailUtility} from "../common/EmailUtility";
import {Broker} from "../../types/Broker";
import {PillButton} from "../common/PillButton";
import {CheckBox} from "../common/CheckBox";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import PhoneInput from 'react-phone-number-input/input'
import {isValidPhoneNumber} from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import _ from "lodash";
import {LeaveWithoutSavingWarningModal} from "../common/LeaveWithoutSavingWarningModal";

interface BrokerFormProps {
    broker?: Broker;
    save: Function;
    cancel: Function;
    isNew?: boolean;
}

interface BrokerFormState {
    name: string;
    first_name: string;
    last_name: string;
    phone: string;
    email: string;
    fleet_size?: number;
    unlimited_trucks: boolean;
    active: boolean;
    loading: boolean;
}

export let BrokerForm = function (props: BrokerFormProps) {
    const broker = _.clone(props.broker) || undefined;
    const initialModelState: BrokerFormState = {
        name: broker?.name || '',
        first_name: broker?.external_contact?.first_name || '',
        last_name: broker?.external_contact?.last_name || '',
        phone: broker?.external_contact?.phone || '',
        email: broker?.external_contact?.email || '',
        fleet_size: broker?.fleet_size,
        unlimited_trucks: broker?.unlimited_trucks ?? false,
        active: broker?.active ?? true,
        loading: false
    };

    const formEngine = useModelFormEngine(
        initialModelState, broker, undefined, validateForm, formStateToSaveData, props.save
    );

    function validateForm(handleValidationResult: Function) {
        return new Promise(function (resolve, reject) {
            let validationErrors = [];
            let invalidFields = [];

            Promise.resolve({})
                .then(function () {
                    if (!formEngine.dataStore.data().name) {
                        validationErrors.push('Please enter a broker name.');
                        invalidFields.push('name');
                    }

                    if (formEngine.dataStore.data().email) {
                        if (!EmailUtility.isValid(formEngine.dataStore.data().email)) {
                            validationErrors.push('Please enter a valid email address.');
                            invalidFields.push('email');
                        }
                    } else {
                        validationErrors.push('Please enter an email address.');
                        invalidFields.push('email');
                    }

                    if (!formEngine.dataStore.data().first_name) {
                        validationErrors.push('Please enter a first name.');
                        invalidFields.push('first_name');
                    }

                    if (!formEngine.dataStore.data().last_name) {
                        validationErrors.push('Please enter a last name.');
                        invalidFields.push('last_name');
                    }

                    if (!formEngine.dataStore.data().phone) {
                        validationErrors.push('Please enter a phone number.');
                        invalidFields.push('phone');
                    } else {
                        if (!isValidPhoneNumber(formEngine.dataStore.data().phone)) {
                            validationErrors.push('Please enter a valid phone number.');
                            invalidFields.push('phone');
                        }
                    }

                    const fleetSize = formEngine.dataStore.data().fleet_size as string | number | null | undefined;
                    if (!formEngine.dataStore.data().unlimited_trucks && (fleetSize === null || fleetSize === undefined || fleetSize === '' || Number(fleetSize) <= 0)) {
                        validationErrors.push('Please enter a max trucks count.');
                        invalidFields.push('fleet_size');
                    }

                    return handleValidationResult(validationErrors, invalidFields, resolve);
                });
        });
    }

    function formStateToSaveData(formStateToTransform: BrokerFormState) {
        let saveData = _.clone(formStateToTransform);
        // Trim the email before persisting so the stored value matches what EmailUtility.isValid actually validated (it trims before testing the regex). Without this, an address like " leading.space@example.com" validates as valid but is saved with the leading space, producing an address that breaks SMTP. Keep this trim localized to the broker email -- do NOT move it into useModelFormEngine#handleTextFieldChange, which runs on every keystroke and would strip spaces as the user types.
        saveData.email = (saveData.email || '').trim();
        return saveData;
    }

    return (
        <div className="common-form-container">
            <ConditionalRenderer
                if={formEngine.dataStore.data().loading}
                renderer={() => (
                    <OverlayLoadingIndicator />
                )}
            />
            <div className="common-form narrow-form broker-form">
                <div className="common-form-body">
                    <div className="common-form-body-row">
                        <div className={formEngine.getFormFieldClasses("common-form-field", "name")}>
                            <div className="common-form-field-label">
                                Broker Name
                            </div>
                            <div className="common-form-field-input-container">
                                <input
                                    id="name"
                                    type="text"
                                    className="common-form-input"
                                    value={formEngine.dataStore.data().name}
                                    onChange={(event) => formEngine.handleTextFieldChange("name", event)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="common-form-body-row">
                        <div className="common-form-body-row-horizontal">
                            <div className="common-form-body-row-horizontal-cell">
                                <div className={formEngine.getFormFieldClasses("common-form-field", "first_name")}>
                                    <div className="common-form-field-label">
                                        Main Contact
                                    </div>
                                    <div className="common-form-field-input-container">
                                        <input
                                            id="first_name"
                                            type="text"
                                            className="common-form-input"
                                            value={formEngine.dataStore.data().first_name}
                                            onChange={(event) => formEngine.handleTextFieldChange("first_name", event)}
                                        />
                                    </div>
                                    <div className="common-form-field-sublabel">
                                        First Name
                                    </div>
                                </div>
                            </div>
                            <div className="common-form-body-row-horizontal-cell">
                                <div className={formEngine.getFormFieldClasses("common-form-field", "last_name")}>
                                    <div className="common-form-field-label">
                                    </div>
                                    <div className="common-form-field-input-container">
                                        <input
                                            id="last_name"
                                            type="text"
                                            className="common-form-input"
                                            value={formEngine.dataStore.data().last_name}
                                            onChange={(event) => formEngine.handleTextFieldChange("last_name", event)}
                                        />
                                    </div>
                                    <div className="common-form-field-sublabel">
                                        Last Name
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="common-form-body-row">
                        <div className="common-form-body-row-horizontal">
                            <div className="common-form-body-row-horizontal-cell">
                                <div className={formEngine.getFormFieldClasses("common-form-field phone-field", "phone")}>
                                    <div className="common-form-field-label">
                                        Phone Number
                                    </div>
                                    <div className="common-form-field-input-container">
                                        <PhoneInput
                                            country="US"
                                            value={formEngine.dataStore.data().phone}
                                            className="common-form-input"
                                            onChange={(value) => formEngine.handleTextFieldChange("phone", {target: {value: value || ''}})}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="common-form-body-row-horizontal-cell">
                                <div className={formEngine.getFormFieldClasses("common-form-field", "email")}>
                                    <div className="common-form-field-label">
                                        Email
                                    </div>
                                    <div className="common-form-field-input-container">
                                        <input
                                            id="email"
                                            type="text"
                                            className="common-form-input"
                                            value={formEngine.dataStore.data().email}
                                            onChange={(event) => formEngine.handleTextFieldChange("email", event)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="common-form-body-row">
                        <div className="common-form-body-row-horizontal">
                            <div className="common-form-body-row-horizontal-cell broker-form-max-trucks-cell">
                                <div className={formEngine.getFormFieldClasses("common-form-field", "fleet_size")}>
                                    <div className="common-form-field-label">
                                        Max Trucks
                                    </div>
                                    <div className="common-form-field-input-container">
                                        <input
                                            id="fleet_size"
                                            type="text"
                                            className="common-form-input fleet-size-input"
                                            value={formEngine.dataStore.data().fleet_size}
                                            disabled={formEngine.dataStore.data().unlimited_trucks}
                                            onChange={(event) => formEngine.handleTextFieldChange("fleet_size", event)}
                                            onBlur={() => formEngine.forceValueToInteger("fleet_size")}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="broker-form-unlimited-cell">
                                <div className="common-form-field-label">
                                    Unlimited
                                </div>
                                <div className="broker-form-unlimited-switch-container">
                                    <Switch
                                        className="broker-form-unlimited-switch"
                                        checked={formEngine.dataStore.data().unlimited_trucks}
                                        onChange={(checked: boolean) => formEngine.updateAndRenderUnsavedData(checked ? {unlimited_trucks: true, fleet_size: 0} : {unlimited_trucks: false})}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="common-form-body-row broker-form-active-checkbox-row">
                        <ConditionalRenderer
                            if={!props.isNew}
                            renderer={() => (
                                <CheckBox
                                    id="broker-form-active-checkbox"
                                    checked={formEngine.dataStore.data().active}
                                    onChange={(checked: boolean) => formEngine.handleCheckboxChange("active", checked)}
                                    label="ACTIVE"
                                />
                            )}
                        />
                    </div>
                    <div className="common-form-body-row">
                        <div className="common-form-action-buttons-row">
                            <PillButton
                                containerClasses="common-form-action-button-container"
                                buttonClasses="solid-green-button submit-button"
                                onClick={formEngine.onClickSave}
                                buttonText="SUBMIT"
                            />
                            <PillButton
                                containerClasses="common-form-action-button-container"
                                buttonClasses="green-border-button cancel-button"
                                onClick={() => props.cancel()}
                                buttonText="CANCEL"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <LeaveWithoutSavingWarningModal navigationBlocker={formEngine.navigationBlocker} />
        </div>
    );
};
